/**
 * Field cleaners shared by the CSV importer and the project-file reader.
 *
 * Every cleaner takes text and returns either a cleaned value or a plain-language reason it was refused.
 * They are strict where a wrong guess would silently corrupt data (dates, money) and tolerant where the
 * meaning is clear (yes/no words, 12-hour times, spreadsheet number formats from many countries).
 */

export type Cleaned<T> = { ok: true; value: T } | { ok: false; message: string };
const ok = <T>(value: T): Cleaned<T> => ({ ok: true, value });
const bad = (message: string): Cleaned<never> => ({ ok: false, message });

/** Control characters other than tab and line breaks have no place in a record. */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

export function cleanText(raw: string, max = 500): Cleaned<string> {
  // A leading apostrophe is how our CSV export protects cells that start with = + - @ from running as formulas.
  const s = raw.replace(CONTROL, "").replace(/^'(?=[=+\-@\t\r])/, "").trim();
  if (s.length > max) return bad(`is longer than ${max} characters`);
  return ok(s);
}

export function cleanEmail(raw: string): Cleaned<string> {
  const t = cleanText(raw, 254);
  if (!t.ok) return t;
  const s = t.value.toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? ok(s) : bad("is not a valid email address");
}

export function cleanPhone(raw: string): Cleaned<string> {
  const t = cleanText(raw, 40);
  if (!t.ok) return t;
  const s = t.value;
  if (!/^[+()\d\s.\-xX#*]+$/.test(s)) return bad("is not a valid phone number");
  const digits = s.replace(/\D/g, "").length;
  return digits >= 5 && digits <= 20 ? ok(s) : bad("is not a valid phone number");
}

export function cleanUrl(raw: string): Cleaned<string> {
  const t = cleanText(raw, 500);
  if (!t.ok) return t;
  try {
    const u = new URL(t.value);
    return u.protocol === "https:" || u.protocol === "http:" ? ok(u.toString()) : bad("must start with http:// or https://");
  } catch { return bad("is not a valid web address"); }
}

export function cleanTimeZone(raw: string): Cleaned<string> {
  const t = cleanText(raw, 60);
  if (!t.ok) return t;
  try { new Intl.DateTimeFormat("en", { timeZone: t.value }); return ok(t.value); } catch { return bad("is not a recognised time zone (use a name like America/New_York)"); }
}

/** `YYYY-MM-DD`, also accepting `/` or `.` between parts. Other layouts (01/02/2026) are refused: they read differently in different countries. */
export function cleanDate(raw: string): Cleaned<string> {
  const m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(raw.trim());
  if (!m) return bad("must be a date written year-month-day, like 2026-10-31");
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (y < 1900 || y > 2200 || dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return bad("is not a real calendar date");
  return ok(`${m[1]}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
}

/** 24-hour `HH:MM`, also accepting `9:30`, `9am` and `9:30 PM`. */
export function cleanTime(raw: string): Cleaned<string> {
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i.exec(raw.trim());
  if (!m) return bad("must be a time like 14:30 or 2:30 PM");
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3]?.toLowerCase();
  if (min > 59) return bad("must be a time like 14:30 or 2:30 PM");
  if (ap) { if (h < 1 || h > 12) return bad("must be a time like 14:30 or 2:30 PM"); h = (h % 12) + (ap === "pm" ? 12 : 0); }
  else if (h > 23) return bad("must be a time like 14:30 or 2:30 PM");
  return ok(`${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`);
}

/**
 * Reads a number the way spreadsheets in different countries write it: `1,234.56`, `1.234,56`, `1 234,56`,
 * `$12`, `(12.50)` for negative. With one separator and exactly three digits after it, a dot is a decimal point
 * and a comma is a thousands mark; repeated separators are thousands marks.
 */
export function parseNumber(input: string): number | null {
  let s = input.trim();
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[\s  ']/g, "");
  if (/^[-−]/.test(s.replace(/^[^\d.,+\-−]+/, ""))) neg = !neg ? true : neg;
  s = s.replace(/[^\d.,]/g, "");
  if (!s || !/\d/.test(s)) return null;
  const dots = (s.match(/\./g) ?? []).length;
  const commas = (s.match(/,/g) ?? []).length;
  let normal: string;
  if (dots && commas) {
    const dec = s.lastIndexOf(".") > s.lastIndexOf(",") ? "." : ",";
    const thou = dec === "." ? "," : ".";
    normal = s.split(thou).join("");
    if (dec === ",") normal = normal.replace(",", ".");
  } else if (commas) {
    normal = commas > 1 || /^\d{1,3}(,\d{3})+$/.test(s) ? s.replace(/,/g, "") : s.replace(",", ".");
  } else if (dots > 1) {
    normal = /^\d{1,3}(\.\d{3})+$/.test(s) ? s.replace(/\./g, "") : "";
  } else normal = s;
  if (!normal) return null;
  const n = Number(normal);
  if (!Number.isFinite(n)) return null;
  return neg ? -n : n;
}

export function cleanNumber(raw: string, min = -1e12, max = 1e12): Cleaned<number> {
  const n = parseNumber(raw);
  if (n === null) return bad("is not a number");
  if (n < min || n > max) return bad(`must be between ${min} and ${max}`);
  return ok(Math.round(n * 1e6) / 1e6);
}

export function cleanInt(raw: string, min = 0, max = 100000): Cleaned<number> {
  const n = parseNumber(raw);
  if (n === null || !Number.isInteger(n)) return bad("must be a whole number");
  if (n < min || n > max) return bad(`must be between ${min} and ${max}`);
  return ok(n);
}

const YES = new Set(["yes", "y", "true", "1", "x", "✓", "✔", "on"]);
const NO = new Set(["no", "n", "false", "0", "off"]);
export function cleanBool(raw: string): Cleaned<boolean> {
  const s = raw.trim().toLowerCase();
  if (YES.has(s)) return ok(true);
  if (NO.has(s)) return ok(false);
  return bad("must be yes or no");
}

export function cleanCurrency(raw: string): Cleaned<string> {
  const s = raw.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(s) ? ok(s) : bad("must be a three-letter currency code like USD or EUR");
}

export function cleanEnum<T extends string>(raw: string, values: readonly T[], synonyms: Record<string, T> = {}): Cleaned<T> {
  const s = raw.trim().toLowerCase().replace(/[\s_-]+/g, " ");
  const direct = values.find((v) => v.replace(/_/g, " ") === s);
  if (direct) return ok(direct);
  const syn = synonyms[s];
  if (syn) return ok(syn);
  return bad(`must be one of: ${values.join(", ")}`);
}
